import { Component, Input } from '@angular/core';
import { WarehousingDashboard } from '../../models/warehousing-dashboard.model';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { WAREHOUSING_CHART_PALETTE } from '../../services/warehousing-chart.service';

/**
 * The four charts on the warehousing dashboard.
 *
 * Split out of the dashboard template so that template stays under the repo's file-length
 * limit, and so `NgxChartsModule` is needed by exactly one component rather than by the
 * whole module.
 *
 * This partial decides nothing about the data — the chart service already decided whether
 * each form is honest against the data it has, and this only draws what it is handed.
 */
@Component({
    selector: 'app-warehousing-charts',
    templateUrl: './warehousing-charts.component.html',
    styleUrls: ['./warehousing-charts.component.scss'],
    standalone: false
})
export class WarehousingChartsComponent {

  @Input()
  dashboard: WarehousingDashboard = new WarehousingDashboard();

  /** Left empty so ngx-charts sizes to its container rather than to a hardcoded box. */
  view: any[] = [];

  /**
   * Only a fallback. Every chart binds `[customColors]` so a hue follows the warehouse
   * rather than its position in the series list — otherwise a filter that drops one site
   * repaints the others and the legend a reader memorised becomes wrong.
   */
  colorScheme: any = { domain: WAREHOUSING_CHART_PALETTE };

  gradient: boolean = false;
  animations: boolean = true;
  showXAxis: boolean = true;
  showYAxis: boolean = true;
  showXAxisLabel: boolean = true;
  showYAxisLabel: boolean = true;
  showLegend: boolean = true;
  legendPosition: string = 'right';
  showPieLabels: boolean = true;
  isDoughnut: boolean = false;
  roundEdges: boolean = false;

  productAxisLabel: string = 'Product';
  qtyAxisLabel: string = 'Qty on hand';
  movementTypeAxisLabel: string = 'Movement type';
  movementCountAxisLabel: string = 'Movements';
  dayAxisLabel: string = 'Day';

  constructor(private stockQuantityService: StockQuantityService) { }

  /**
   * Total behind the stock-share pie, so the table twin states the whole a slice is part of.
   * A percentage with no denominator is not checkable.
   */
  get stockShareTotal(): number {
    return this.stockQuantityService.round(
      this.dashboard.stockShareByWarehouse.data.reduce((sum: number, row: any) => sum + Number(row.value), 0));
  }

  /** Share of the plotted whole, for the table twin beside the pie. */
  sharePercent(value: number): string {
    const total: number = this.stockShareTotal;

    if (this.stockQuantityService.isZero(total)) {
      return '—';
    }

    return (Number(value) / total * 100).toFixed(1) + '%';
  }

  /** Row total for a stacked group, so the table twin is readable without adding up cells. */
  groupTotal(group: any): number {
    return group.series.reduce((sum: number, row: any) => sum + Number(row.value), 0);
  }

  /**
   * Axis ticks are counts of movements, so a fractional tick is meaningless. ngx-charts
   * would otherwise label 0, 0.5, 1 on a chart whose tallest bar is 2.
   */
  wholeNumberTick(value: any): string {
    const n: number = Number(value);

    return Number.isInteger(n) ? String(n) : '';
  }

  /** Colour chip for the table twin, so identity is never carried by the hue alone. */
  colorFor(chart: any, name: string): string {
    const match: any = chart.customColors.filter((c: any) => c.name === name)[0];

    return match ? match.value : 'transparent';
  }

  /**
   * The full entity name for a charted label.
   *
   * The charts plot a prefix-stripped label because ngx-charts trims legend text to 10
   * characters, which turned every "Blacks ... Depot" into the same string. The table twin
   * is not trimmed, so it shows the real name.
   */
  fullLabel(chart: any, name: string): string {
    return chart.labelMap && chart.labelMap[name] ? chart.labelMap[name] : name;
  }

  isNegative(qty: number): boolean {
    return this.stockQuantityService.isNegative(qty);
  }

  formatQty(qty: number): string {
    return this.stockQuantityService.round(qty).toString();
  }
}
